<template>
  <div class="carbon_path_container">
    <div class="carbon_path">
      <!-- 头部 -->
      <header class="cp_header">
        <div class="cp_header_text">
          <h1 id="carbon-path-title" class="cp_title">{{ $t('carbonPath.marketTitle') }}</h1>
          <p class="cp_subtitle">{{ $t('carbonPath.marketSubtitle') }}</p>
        </div>
        <div class="cp_header_right">
          <el-input
            v-model="searchKeyword"
            class="cp_search"
            :placeholder="$t('carbonPath.searchPlaceholder')"
            prefix-icon="el-icon-search"
            clearable 
          />
        </div>
      </header>

      <!-- 主体 -->
      <div class="cp_body">
        <!-- 卡片（有搜索关键词时隐藏，仅展示列表） -->
        <section v-if="!hasSearchQuery && featuredCards.length" class="cp_featured">
          <div
            v-for="card in featuredCards"
            :key="card.code"
            :class="['cp_featured_card', card.className]"
            @click="clickFeaturedCard(card.code)"
          >
            <img class="cp_featured_icon" :src="card.img" :alt="card.alt" />
            <div class="cp_featured_title">{{ $t(card.titleKey) }}</div>
            <div class="cp_featured_desc">{{ $t(card.descKey) }}</div>
          </div>
        </section>
        
        <!-- 产品分类 -->
        <div v-if="!hasSearchQuery" class="cp_tabs">
          <button
            v-for="tab in categoryTabs"
            :key="tab.id"
            class="cp_tab"
            :class="{ 'is-active': activeCategory === tab.id }"
            @click="setActiveCategory(tab.id)"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- 产品标签 -->
        <!-- <div v-if="!hasSearchQuery" class="cp_tags">
          <button
            v-for="tag in tags"
            :key="tag.id"
            class="cp_tag"
            :class="{ 'is-active': activeTagId === tag.id }"
            @click="activeTagId = tag.id"
          >
            {{ tag.label }}
          </button>
        </div> -->

        <!-- 产品列表 -->
        <div v-loading="loading" class="cp_list_scroll">
          <div v-if="!loading && filteredApps.length === 0" class="cp_app_empty">
            未找到任何应用。
          </div>
          <div v-else class="cp_app_grid">
            <div
              v-for="item in filteredApps"
              :key="item.id"
              class="cp_app_card"
              @click="openProductDetail(item)"
            >
              <div class="cp_app_icon">
                <img class="cp_app_icon_img" :src="item.file_url" alt="" />
              </div>
              <div class="cp_app_meta">
                <div class="cp_app_name">{{ item.title }}</div>
                <p class="cp_app_desc">{{ item.displayDescription }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 产品详情弹窗 -->
      <el-dialog
        :visible.sync="detailVisible"
        width="25vw"
        top="26vh"
        custom-class="cp_detail_dialog"
        append-to-body
        :close-on-click-modal="false"
        @closed="onDetailClosed"
      >
        <div v-if="detailProduct" slot="title" class="cp_detail_modal_title">
          <div class="cp_detail_title_icon">
            <img
              v-if="detailProduct.file_url"
              class="cp_detail_title_icon_img"
              :src="detailProduct.file_url"
              alt=""
            />
          </div>
          <div class="cp_detail_title_line">
            <span class="cp_detail_modal_name">{{ detailProduct.title }}</span>
            <template v-if="detailProduct.category_label">
              <span class="cp_detail_modal_sep" aria-hidden="true">|</span>
              <span class="cp_detail_modal_cat">{{
                detailProduct.category_label
              }}</span>
            </template>
          </div>
        </div>
        <div v-if="detailProduct" class="cp_detail_card">
          <div v-if="detailTagList.length" class="cp_detail_tags_wrap">
            <span
              v-for="tg in detailTagList"
              :key="String(tg.id) + (tg.code || '')"
              class="cp_detail_tag"
            >
              {{ tg.name || tg.code }}
            </span>
          </div>
          <p class="cp_detail_desc">{{ detailBodyText }}</p>
          <div class="cp_detail_footer">
            <el-button
              type="primary"
              round
              class="cp_detail_enter_btn"
              :disabled="!canEnterDetailProduct(detailProduct)"
              native-type="button"
              @click="enterProduct(detailProduct)"
            >
              {{ getDetailEnterText(detailProduct) }}
            </el-button>
          </div>
        </div>
      </el-dialog>

      <!-- 交通查询助手弹窗 -->
      <el-dialog
        :visible.sync="trafficAssistantVisible"
        custom-class="cp_traffic_dialog"
        append-to-body
        width="30vw"
        top="12vh"
        :close-on-click-modal="false"
        @closed="onTrafficAssistantClosed"
      >
        <TrafficAssistant v-if="trafficAssistantVisible" />
      </el-dialog>
    </div>
  </div>

</template>

<script>
import { getCategoryAndTag, getOwnerFeatures, getPartnerProducts } from '@/api/index';
import { mapMutations } from 'vuex';
import chatTpcIcon from '@/assets/images/chat_tpc.png';
import chatTzjIcon from '@/assets/images/chat_tzj.png';
import chatCbamIcon from '@/assets/images/chat_cbam.png';
import TrafficAssistant from '@/components/CarbonPath/TrafficAssistant.vue';

const TAG_ALL = '__all__'; // 前端「全部」标签（接口无此 id）
const CATEGORY_ALL = 0; // 前端「全部」分类（接口无此 id）
const CATEGORY_ORDER = [0, 1, 2, 3, 4]; // 产品分类顺序，0 为全部
const FEATURED_CARD_CONFIGS = [
  {
    code: 'tpc',
    img: chatTpcIcon,
    alt: '碳盘查',
    className: 'cp_featured_card--tpc',
    titleKey: 'carbonPath.featuredTpcTitle',
    descKey: 'carbonPath.featuredTpcDesc',
  },
  {
    code: 'tzj',
    img: chatTzjIcon,
    alt: '碳足迹',
    className: 'cp_featured_card--tzj',
    titleKey: 'carbonPath.featuredTzjTitle',
    descKey: 'carbonPath.featuredTzjDesc',
  },
  {
    code: 'cbam',
    img: chatCbamIcon,
    alt: 'CBAM Agent',
    className: 'cp_featured_card--cbam',
    titleKey: 'carbonPath.featuredCbamTitle',
    descKey: 'carbonPath.featuredCbamDesc',
  },
];
// 产品分类标签
const CATEGORY_LABEL_KEYS = {
  0: 'carbonPath.categoryAll',
  1: 'carbonPath.categoryAgent',
  2: 'carbonPath.categorySaas',
  3: 'carbonPath.categoryConsulting',
  4: 'carbonPath.categoryCert',
};
// 可进入产品详情的产品代码
const DETAIL_ENTER_PRODUCT_CODES = new Set(['tpc', 'tzj', 'zsk', 'jcz']);

export default {
  name: 'CarbonPath',
  components: { TrafficAssistant },
  data() {
    return {
      searchKeyword: '', // 搜索关键词
      loading: false, // 加载状态
      allProducts: [], // 所有产品
      categoryTagMap: {}, // 产品分类标签映射
      activeCategory: CATEGORY_ALL, // 当前产品分类，默认「全部」
      activeTagId: TAG_ALL, // 当前产品标签
      detailVisible: false, // 详情弹窗是否显示
      detailProduct: null, // 当前产品
      trafficAssistantVisible: false, // 交通查询助手弹窗
    };
  },
  computed: {
    // 产品分类
    categoryTabs() {
      return CATEGORY_ORDER.map((id) => ({
        id,
        label: this.$t(CATEGORY_LABEL_KEYS[id]),
      }));
    },
    // 产品标签（接口：每个分类下有若干标签；“全部”不筛标签）
    tags() {
      const allTag = {
        id: TAG_ALL,
        code: null,
        label: this.$t('carbonPath.tagAll'),
      };
      const key = String(this.activeCategory);
      const apiTags = this.categoryTagMap[key];
      const list = Array.isArray(apiTags) ? apiTags : [];
      const mapped = list.map((t) => ({
        id: String(t.id),
        code: t.code != null ? String(t.code).toUpperCase() : '',
        label: t.name,
      }));
      return [allTag, ...mapped];
    },
    // 是否有搜索关键词（有则全局搜全站产品并隐藏分类/标签区）
    hasSearchQuery() {
      return (this.searchKeyword || '').trim().length > 0;
    },
    // 主推卡片：仅当接口返回对应 code 时才显示
    featuredCards() {
      const availableCodes = new Set(
        this.allProducts.map((item) => String(item.code || '').toLowerCase().trim()),
      );
      return FEATURED_CARD_CONFIGS.filter((card) => availableCodes.has(card.code));
    },
    // 过滤后的产品列表
    filteredApps() {
      const q = (this.searchKeyword || '').trim().toLowerCase();
      const fallbackDesc = this.$t('carbonPath.detailDescFallback');

      let list;
      if (q) {
        // 全局搜索：不受当前分类、标签限制
        list = this.allProducts.slice();
      } else {
        const cat = Number(this.activeCategory);
        // 分类 0：前端「全部」，展示所有产品
        list =
          cat === CATEGORY_ALL
            ? this.allProducts.slice()
            : this.allProducts.filter((item) => Number(item.category) === cat);

        // 同一商品只属于一个分类，但可同时挂多个标签；选中某一标签时，只要 tags 中任意一项命中即可（OR）。
        // 兼容大整数 id 在 JSON 中精度不一致：辅以 code（如 ENT/PRC）匹配。
        if (this.activeTagId !== TAG_ALL) {
          const tagIdStr = String(this.activeTagId);
          const sel = this.tags.find((t) => t.id === this.activeTagId);
          const tagCodeSel = sel && sel.code ? String(sel.code).toUpperCase() : '';
          list = list.filter(
            (item) =>
              Array.isArray(item.tags) &&
              item.tags.some((t) =>
                productTagMatchesFilter(t, tagIdStr, tagCodeSel),
              ),
          );
        }
      }

      list = list.map((item) => ({
        ...item,
        iconVariant: item.code,
        displayDescription: item.description || fallbackDesc,
      }));

      if (!q) return list;

      return list.filter((item) => itemMatchesGlobalSearch(item, q));
    },
    // 当前产品标签列表
    detailTagList() {
      const p = this.detailProduct;
      if (!p || !Array.isArray(p.tags)) return [];
      return p.tags.filter((t) => t && (t.name || t.code));
    },
    // 当前产品描述
    detailBodyText() {
      const p = this.detailProduct;
      if (!p) return '';
      return p.description || this.$t('carbonPath.detailDescFallback');
    },
  },
  mounted() {
    // 加载碳行应用市场数据
    this.loadCarbonPathData();
  },
  methods: {
    ...mapMutations(['setAccessType']),
    // 点击卡片
    clickFeaturedCard(code) {
      switch (code) {
        case 'tpc':
          this.setAccessType('tpc');
          this.$root.$emit('close-carbon-path'); // 关闭碳行
          this.$root.$emit("start-new-chat"); // 将事件广播到全局
          break;
        case 'tzj':
          this.setAccessType('tzj');
          this.$root.$emit('close-carbon-path'); // 关闭碳行
          this.$root.$emit("start-new-chat"); // 将事件广播到全局
          break;
        case 'zsk':
          this.$root.$emit('close-carbon-path'); // 关闭碳行
          this.$router.push({ name: 'KnowledgeBase' });
          break;
        case 'cbam':
          // 弹出CBAM Agent介绍弹窗
          this.detailProduct = { 
            title: 'CBAM Agent',
            description: '深度嵌入欧盟最新CBAM实施细则及默认值数据库。自动解析企业生产工序、能耗数据及物料流，精准完成隐含碳排放的量化核算与数据填报，智能生成符合欧盟进口商要求的CBAM报告。',
            code: 'cbam',
            category: 1,
            category_label: '智能体',
            tags: [
              { name: '企业'  },
              { name: '从业者' },
              { name: '行业协会' },
            ],
            file_url: chatCbamIcon,
           };
          this.detailVisible = true;
          break;
      }
    },
    // 设置当前产品分类
    setActiveCategory(id) {
      this.activeCategory = id;
      this.activeTagId = TAG_ALL;
    },
    // 合并去重产品列表
    mergeDedupe(features, partnerItems) {
      const map = new Map();
      [...features, ...partnerItems].forEach((item) => {
        if (item && item.id != null && !map.has(String(item.id))) {
          map.set(String(item.id), item);
        }
      });
      return Array.from(map.values());
    },
    // 加载碳行应用市场数据
    async loadCarbonPathData() {
      this.loading = true;
      try {
        const [catMap, featRes, partnerOutcome] = await Promise.all([
          getCategoryAndTag().catch(() => {
            this.$message.error(this.$t('carbonPath.categoriesLoadFailed'));
              return null;
            }),
          getOwnerFeatures().catch(() => {
            this.$message.error(this.$t('carbonPath.productsLoadFailed'));
            return null;
          }),
          getPartnerProducts().catch(() => {
            this.$message.error(this.$t('carbonPath.productsLoadFailed'));
            return null;
          }),
        ]);

        this.categoryTagMap = catMap ? catMap.data.data : {};
        const features = featRes ? featRes.data.data : [];
        const partnerItems = partnerOutcome ? partnerOutcome.data.data.items : [];

        this.allProducts = this.mergeDedupe(features, partnerItems);
      } finally {
        this.loading = false;
      }
    },
    // 打开产品详情
    openProductDetail(item) {
      if (!item) return;
      this.detailProduct = { ...item };
      this.detailVisible = true;
    },
    // 详情弹窗：仅 code 为 tpc / tzj / zsk 可进入（与主推卡片一致）
    canEnterDetailProduct(item) {
      if (!item || item.code == null) return false;
      const c = String(item.code).toLowerCase().trim();
      return DETAIL_ENTER_PRODUCT_CODES.has(c);
    },
    // 获取详情弹窗进入文本
    getDetailEnterText(item) {
      if (!item || item.code == null) return '';
      const c = String(item.code).toLowerCase().trim();
      switch (c) {
        case 'tpc':
          return this.$t('carbonPath.detailEnter');
        case 'tzj':
          return this.$t('carbonPath.detailEnter');
        case 'zsk':
          return this.$t('carbonPath.detailEnter');
        case 'jcz':
          return this.$t('carbonPath.detailEnter');
        case 'eco':
          return this.$t('carbonPath.mobileApp');
        default:
          return this.$t('carbonPath.detailNotOpenYet');
      }
    },
    // 关闭产品详情
    onDetailClosed() {
      this.detailProduct = null;
    },
    // 进入产品（仅允许 tpc / tzj / zsk / jcz）
    enterProduct(item) {
      if (!this.canEnterDetailProduct(item)) return;
      const code = String(item.code).toLowerCase().trim();
      switch (code) {
        case 'tpc':
          this.$root.$emit('close-carbon-path');
          this.setAccessType('tpc');
          this.$root.$emit('start-new-chat'); // 将事件广播到全局
          break;
        case 'tzj':
          this.$root.$emit('close-carbon-path');
          this.setAccessType('tzj');
          this.$root.$emit('start-new-chat'); // 将事件广播到全局
          break;
        case 'zsk':
          this.$root.$emit('close-carbon-path');
          this.$router.push({ name: 'KnowledgeBase' });
          break;
        case 'jcz':
          this.trafficAssistantVisible = true;
          return;
        default:
          break;
      }
      this.detailVisible = false;
    },
    onTrafficAssistantClosed() {
      // this.detailVisible = false;
      this.trafficAssistantVisible = false;
    },
  },
};

/** 判断是否命中所选标签（id 精确一致，失败时用 code） */
function productTagMatchesFilter(productTag, selectedIdStr, selectedCodeUpper) {
  const id = productTag && productTag.id != null ? String(productTag.id) : '';
  if (selectedIdStr && id === selectedIdStr) return true;
  const code =
    productTag && productTag.code != null
      ? String(productTag.code).toUpperCase().trim()
      : '';
  if (selectedCodeUpper && code === selectedCodeUpper) return true;
  return false;
}

/** 全局搜索：标题、描述、编码、分类名、标签名/编码 */
function itemMatchesGlobalSearch(item, qLower) {
  const title = (item.title || '').toLowerCase();
  const desc = (item.description || '').toLowerCase();
  const code = (item.code || '').toLowerCase();
  const catLabel = (item.category_label || '').toLowerCase();
  let tagBlob = '';
  if (Array.isArray(item.tags)) {
    tagBlob = item.tags
      .map((t) => `${t && t.name != null ? t.name : ''} ${t && t.code != null ? t.code : ''}`)
      .join(' ')
      .toLowerCase();
  }
  return (
    title.includes(qLower) ||
    desc.includes(qLower) ||
    code.includes(qLower) ||
    catLabel.includes(qLower) ||
    tagBlob.includes(qLower)
  );
}
</script>

<style scoped lang="scss">
*{
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.carbon_path_container {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #fff;
  box-sizing: border-box;
  overflow-y: hidden;
}

.carbon_path {
  width: 700px;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 50px;
  box-sizing: border-box;
}

// 头部
.cp_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  margin-bottom: 32px;
  gap: 60px;
}

.cp_header_text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .cp_title {
    font-size: 20px;
    font-weight: 700;
    color: rgba(68, 77, 85, 1);
  }

  .cp_subtitle {
    font-size: 16px;
    font-weight: 400;
    color: rgba(113, 128, 142, 1);
  }
}

.cp_header_right {
  display: flex;
  align-items: center;
  flex-shrink: 0;

  .cp_search {
    width: 240px;
  }

  .cp_search ::v-deep .el-input__inner {
    border-radius: 999px;
    height: 37px;
    line-height: 37px;
    border-color: rgba(170, 179, 187, 1);
    padding-left: 37px;
  }

  .cp_search ::v-deep .el-input__prefix {
    left: 14px;
    color: rgba(170, 179, 187, 1);
  }
}

// 产品卡片
.cp_featured {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  flex-shrink: 0;
  margin-bottom: 40px;
}

.cp_featured_card {
  min-height: 240px;
  border-radius: 24px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: flex-start;
  color: rgba(255, 255, 255, 1);
  cursor: pointer;
  transition: transform 0.2s;

  .cp_featured_icon {
    width: 40px;
    height: 40px;
    object-fit: cover;
  }

  .cp_featured_title {
    font-size: 20px;
    font-weight: 700;
  }

  .cp_featured_desc {
    line-height: 1.5;
    font-size: 12px;
    font-weight: 700;
    text-align: justify;
  }
}

.cp_featured_card--tpc {
  background-image: url('@/assets/images/carbonPath_bg_tpc.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.cp_featured_card--tzj {
  background-image: url('@/assets/images/carbonPath_bg_tzj.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.cp_featured_card--cbam {
  background-image: url('@/assets/images/carbonPath_bg_zsk.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.cp_featured_card:hover {
  transform: translateY(-10px);
}

// 产品列表
.cp_body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.cp_tabs {
  display: flex;
  gap: 24px;
  flex-shrink: 0;
  margin-bottom: 24px;
}

.cp_tab {
  height: 32px;
  border: none;
  background: transparent;
  padding: 4px 16px;
  font-size: 16px;
  color: rgba(90, 102, 114, 1);
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s, color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cp_tab:hover {
  background: rgba(233, 239, 255, 1);
}

.cp_tab.is-active {
  background: rgba(233, 239, 255, 1);
}

.cp_tags {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-bottom: 16px;
}

.cp_tag {
  border: 1px solid rgba(6, 10, 38, 0.08);
  background: #fff;
  padding: 4px 16px;
  font-size: 13px;
  color: rgba(6, 10, 38, 0.55);
  cursor: pointer;
  border-radius: 999px;
  transition:
    background 0.2s,
    color 0.2s,
    border-color 0.2s;
}

.cp_tag:hover {
  color: rgba(6, 10, 38, 0.75);
  border-color: rgba(6, 10, 38, 0.12);
  background: rgba(6, 10, 38, 0.03);
}

.cp_tag.is-active {
  border-color: rgba(59, 140, 255, 0.35);
  background: rgba(59, 140, 255, 0.1);
  color: #2563eb;
}

.cp_list_scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
}

.cp_app_empty {
  font-size: 16px;
  font-weight: 400;
  color: rgba(113, 128, 142, 1);
  text-align: center;
  padding: 20px;
}

.cp_app_grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;
}

.cp_app_card {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 14px;
  // border: 1px solid rgba(6, 10, 38, 0.06);
  // background: #fafbfc;
  // transition: box-shadow 0.2s, border-color 0.2s;
  cursor: pointer;
}

.cp_app_card:hover {
  background-color: rgba(232, 239, 255, 1);
  // box-shadow: 0 4px 16px rgba(6, 10, 38, 0.06);
}

.cp_app_icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.cp_app_icon_img {
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.cp_app_meta {
  min-width: 0;
}

.cp_app_name {
  font-size: 16px;
  font-weight: 700;
  color: rgba(6, 10, 38, 0.88);
  margin-bottom: 6px;
}

.cp_app_desc {
  font-size: 13px;
  line-height: 1.5;
  color: rgba(6, 10, 38, 0.45);
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: justify;
}
</style>

<style lang="scss">
/* 详情弹窗append-to-body */
.cp_detail_dialog.el-dialog {
  border-radius: 24px;
  overflow: hidden;
}

.cp_detail_dialog .el-dialog__header {
  padding: 24px 24px 14px;
}

.cp_detail_dialog .el-dialog__headerbtn {
  top: 32px;
  right: 20px;
  font-size: 24px;
}

.cp_detail_dialog .el-dialog__title {
  width: 100%;
  padding: 0;
  margin: 0;
  font-size: 0;
  line-height: 0;
}

.cp_detail_modal_title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.cp_detail_title_icon {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cp_detail_title_icon_img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.cp_detail_title_line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
  min-width: 0;
}

.cp_detail_modal_name {
  font-size: 17px;
  font-weight: 700;
  color: #262626;
  line-height: 1.35;
  word-break: break-word;
}

.cp_detail_modal_sep {
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.22);
  font-size: 16px;
  font-weight: 300;
}

.cp_detail_modal_cat {
  font-size: 14px;
  font-weight: 500;
  color: #79808c;
  line-height: 1.4;
}

.cp_detail_dialog .el-dialog__body {
  padding: 2px 24px 26px;
}

.cp_detail_card {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cp_detail_tags_wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.cp_detail_tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 12px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(24, 161, 246, 1);
  background: rgba(209, 236, 253, 1);
  border-radius: 8px;
  border: 1px solid rgba(24, 161, 246, 1);
}

.cp_detail_desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: rgba(113, 128, 142, 1);
  white-space: pre-wrap;
  text-align: justify;
}

.cp_detail_footer {
  display: flex;
  justify-content: center;
}

.cp_detail_enter_btn.el-button.el-button--primary {
  min-width: 100px;
  height: 40px;
  padding: 0 36px;
  font-size: 15px;
  font-weight: 600;
  background: rgba(24, 161, 246, 1);
  border: none;
  border-radius: 8px;

  &:hover {
    background: rgb(111, 200, 255);
  }
}

.cp_detail_enter_btn.el-button.el-button--primary.is-disabled,
.cp_detail_enter_btn.el-button.el-button--primary.is-disabled:hover {
  background: #f0f2f5 !important;
  border: 1px solid rgba(6, 10, 38, 0.08);
  color: rgba(6, 10, 38, 0.38);
}

.cp_traffic_dialog.el-dialog {
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 0;
}

.cp_traffic_dialog .el-dialog__body {
  padding: 0;
  height: 680px;
  overflow: hidden;
}

.cp_traffic_dialog .el-dialog__header {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 10;
  padding: 0;
  background: transparent;
  border: none;
  pointer-events: none;
}

.cp_traffic_dialog .el-dialog__headerbtn {
  top: 14px;
  right: 14px;
  pointer-events: auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;

  .el-dialog__close {
    color: #606266;
    font-weight: bold;
  }

  &:hover .el-dialog__close {
    color: #303133;
  }
}

.cp_traffic_dialog .el-dialog__title {
  display: none;
}
</style>